import * as React from 'react';
import { CreateButton, Panel, Spinner, Switch } from '@ixicare/ui';
import { Link } from 'react-router-dom';
import { observer } from 'mobx-react';
import store from '~/store';
import { t } from '~i18n';
import { useStores } from '~/store/utils';
import OrganizationTree from '../components/OrganizationTree';
import { flattenOrganizations, useOrganizations } from '../hooks/useOrganizations';
import styles from './SiteList.module.css';

const SiteSettingsPage = observer(() => {
  const { sites, permissions, auth } = useStores();
  const [editModeRequested, setEditModeRequested] = React.useState(false);
  const canManage = permissions.has('can_manage_organizations');
  const editMode = canManage && editModeRequested;

  const organizationsQuery = useOrganizations();

  React.useEffect(() => {
    store.sites.setSelectedSiteUuid(null);
    sites.loadSites();
  }, [sites]);

  const orgs = organizationsQuery.data ?? [];

  const siteUuidToId = React.useMemo(() => {
    const map = new Map<string, number>();
    sites.sites.forEach((s) => map.set(s.uuid, s.id));
    return map;
  }, [sites.sites]);

  const assignedSiteUuids = React.useMemo(
    () => new Set(flattenOrganizations(orgs).flatMap(({ node }) => node.sites.map((s) => s.uuid))),
    [orgs]
  );

  const unassignedSites = React.useMemo(
    () => sites.sites.filter((s) => !assignedSiteUuids.has(s.uuid)),
    [sites.sites, assignedSiteUuids]
  );

  const loading = sites.loading || organizationsQuery.isLoading;

  return (
    <>
      <Panel>
        <Panel.Title
          extraInfo={
            canManage ? (
              <span className={styles.EditToggle}>
                {t('siteSettings.organization.editToggle')}
                <Switch label="" checked={editMode} onChange={setEditModeRequested} />
              </span>
            ) : undefined
          }
        >
          {t('siteSettings.allBuildings')}
        </Panel.Title>
        {loading ? (
          <Spinner />
        ) : (
          <OrganizationTree
            organizations={orgs}
            unassignedSites={unassignedSites}
            editMode={editMode}
            scopeRootId={auth.user?.permissionOrganizationId ?? null}
            siteUuidToId={siteUuidToId}
            onSitesChanged={() => sites.reloadSites()}
          />
        )}
        <Link to="/sites/create">
          <CreateButton atBottom />
        </Link>
      </Panel>
    </>
  );
});

export default SiteSettingsPage;
